import React from 'react';
import PropTypes from 'prop-types';
import {Button, Card, List} from 'antd';
import {LinesContext, UserContext, WorkflowsContext} from '@web/hooks/context';
import Wrapper from "./components/Wrapper";
import Header from './components/Header';
import AgentBuilder from "./components/AgentBuilder";
import InputsManager from "./components/InputsManager";
import {CheckIcon, DeleteIcon, EditIcon} from '@web/assets/icons';
import {notifyAndTrack} from '@web/components/notification';
import {useSelector} from 'react-redux';
// import UserAvatar from '@web/components/UserAvatar';
import Icon from '@web/components/Icon';
import ComposioConnectButton from '@web/components/ComposioConnectButton';
import ItemApi from '@web/lib/ItemApi';
import AppsServicesSelector from '@web/components/AppsServicesSelector';

Action.propTypes = {
  action: PropTypes.shape({}),
  isActive: PropTypes.bool,
  isMobile: PropTypes.bool,
  height: PropTypes.number,
  pauseAnimation: PropTypes.func,
  startAnimation: PropTypes.func,
  onExit: PropTypes.func,
  navigatePrev: PropTypes.func,
  markCompleteAndNavigateNext: PropTypes.func,
  onServiceViewChange: PropTypes.func,
  onSelect: PropTypes.func,
  onClearSelect: PropTypes.func,
  activeServiceView: PropTypes.string,
  stepIndex: PropTypes.number.isRequired,
};

function Action(props) {
  const [, h] = React.useContext(UserContext);
  const wtx = React.useContext(WorkflowsContext);
  const ctx = React.useContext(LinesContext);
  const activeActionIndex = useSelector((state) => state.AI[ctx.collectionId]?.activeIndex)
  const [error, setError] = React.useState("");
  const isSelected = activeActionIndex === props.stepIndex;
  const actionAPI = new ItemApi(`${h}/${wtx.workflowId}/steps/${props.stepIndex}`);
  const workflowAPI = new ItemApi(`/${h}/${wtx.workflowId}`);

  const onFocus = () => {
    props?.pauseAnimation?.();
  }

  const onBlur = () => {
    props?.startAnimation?.();
  }

  const onError = (error) => {
    setError(error);
  }

  const onClickDelete = () => {
    actionAPI.delete()
    .then(() => {
      return workflowAPI.update({
        steps: wtx
        .workflow
        .steps
        .filter((step, index) =>
          index !== props.stepIndex),
      }, () => notifyAndTrack.success("Removed step"));
    });
  }

  const onEditContext = () => {
    console.log("edit context");
  }

  const onClickResetAction = () => {
    actionAPI
    .update({
      ...wtx
      .stepHelpers
      .find((action) =>
        action.method === props.action.method),
    }, () => notifyAndTrack.success("Reset step"))
  }

  const onAddActionInput = (pathToAdd) => {
    actionAPI
    .update({
      inputPaths: [
        ...props.action.inputPaths || [],
        pathToAdd,
      ],
    }, () => notifyAndTrack.success("Added"))
  };

  const onRemoveActionInput = (pathToRemove) => {
    actionAPI
    .update({
      inputPaths: props.action.inputPaths.filter((path) => path !== pathToRemove),
    }, () => notifyAndTrack.success("Removed"));
  }

  const onChangePerformance = (modelPerformance) =>
    actionAPI
    .update({
      modelPerformance
    }, () => notifyAndTrack.success("Nice"));

  /**
   * @function onMediaReady
   * @type {(function(...filePaths:string): void)|*}
   */
  const onMediaReady = React.useCallback((...filePaths) => {
    console.log("onMediaReady:", filePaths);
  }, []);

  return <Wrapper
    isMobile={props.isMobile}
    height={props.height}
  >
      <Card
        style={{width: "100%", maxWidth: 900}}
        title={
          <Header
            icon={props.action.icon}
            logo={props.action.logo}
            stepNumber={props.stepIndex + 1}
            text={props.action?.type}
            subtitle={props.action?.method?.replace(":", " ")}
          />}
        extra={
          <div className="space space-between">
            <div className="space space-center-y">
              {props.action.authOptions?.some?.((option) => option.provider === "composio") &&
                <ComposioConnectButton key={props.action.integrationPath} appId={props.action.integrationPath.split("/").pop()}>
                  Connect
                </ComposioConnectButton>}
            </div>
          </div>
        }
        className={`card-simple card-rounded card-h-500 card-no-separator${isSelected ? " card-highlight" : ""}`}
        bordered={isSelected}
      >
        <div className="space space-col">
          <div className="space space-between space-center-y">
            <div className="space space-center-y">
              {isSelected ?
                <Button size="large" className="icon-btn" onClick={() => props.onClearSelect(props.stepIndex)}>
                  <span className="green-color">Ok</span> <CheckIcon className="green-icon" />
                </Button>:
                <Button size="large" className="icon-btn" onClick={() => props.onSelect(props.stepIndex)}>
                  <span className="orange-color">Edit</span> <EditIcon className="orange-icon" />
                </Button>
              }
              <AppsServicesSelector
                placeholder={"Inputs"}
                action={props.action}
                onChange={onAddActionInput}
                excludeIndex={[props.stepIndex]}
              />
            </div>
            <div className="space space-center-y">
              <Button.Group size="large">
                <Button
                  className="icon-btn"
                  title="Use global context data in this action"
                  onClick={() =>
                    actionAPI.update({
                      useContext: checked,
                    }, () => notifyAndTrack.success("Nice"))}
                >
                  <Icon name="TbStackPush" size={24} />
                </Button>
                <Button
                  className="icon-btn"
                  title="Update context with the result of this action"
                  onClick={() =>
                    actionAPI.update({
                      updateContext: checked,
                    }, () => notifyAndTrack.success("Nice"))}
                >
                  <Icon name="TbStackPop" size={24} />
                </Button>
              </Button.Group>
              <Button.Group size="large">
                <Button key="fastest" size="large" disabled={props.modelPerformance === "model:fastest"} className="icon-btn" onClick={(e) => onChangePerformance("model:fastest")}>
                  Fastest</Button>
                <Button key="standard" size="large" disabled={props.modelPerformance === "model:standard"} className="icon-btn" onClick={(e) => onChangePerformance("model:standard")}>
                  Standard</Button>
                <Button key="pro" size="large" disabled={props.modelPerformance === "model:pro"} className="icon-btn" onClick={(e) => onChangePerformance("model:pro")}>
                  Pro</Button>
              </Button.Group>
              <Button size="large" className="icon-btn" onClick={onClickResetAction}>
                <Icon name="TbRefresh" />
              </Button>
              <Button size="large" className="icon-btn" onClick={onClickDelete}>
                <DeleteIcon />
              </Button>
            </div>
          </div>
          {props.action?.inputPaths?.length &&
            <InputsManager
              action={props.action}
              onClick={onRemoveActionInput}
            />
          }
          <AgentBuilder
            key="agent"
            onSave={(instructions) => {
              actionAPI
              .update({
                instructions,
              }, () => notifyAndTrack.success("Nice"));
            }}
            instructions={props.action.instructions}
            tip={props.action.tip}
            onFocus={onFocus}
            onBlur={onBlur}
            onError={onError}
            onMediaReady={onMediaReady}
            onSelect={props.onSelect}
            selected={isSelected}
          >
            <List
              header=""
              size="small"
              dataSource={
                wtx
                .stepHelpers
                .find((action) =>
                  action.method === props.action.method &&
                  action.type === props.action.type)
                ?.validators?.map((instruction, index) =>
                  <label key={index}>{instruction}</label>)
              }
              renderItem={(instruction, index) => (
                <List.Item key={index}>
                  <List.Item.Meta
                    description={<span className="font-lg ai-text">{instruction}</span>}
                  />
                </List.Item>
              )}
            />
          </AgentBuilder>
        </div>
      </Card>
  </Wrapper>;
}

export default Action;
